"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoKeywordOpportunityRanker = exports.seoKeywordOpportunityRankerResultSchema = exports.seoKeywordOpportunityRankerArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Input schema for keyword data from any source
var keywordDataSchema = zod_1.z.object({
    keyword: zod_1.z.string().describe('Keyword phrase'),
    searchVolume: zod_1.z.number().min(0).describe('Monthly search volume'),
    keywordDifficulty: zod_1.z.number().min(0).max(100).describe('Keyword difficulty (0-100)'),
    costPerClick: zod_1.z.number().min(0).describe('Cost per click (CPC)'),
    competition: zod_1.z
        .number()
        .min(-1)
        .max(1)
        .describe('Competition level (0-1). Placeholder value -1 indicates data unavailable (not used in scoring)'),
    searchIntent: zod_1.z
        .string()
        .describe('Search intent classification. Placeholder value "unknown" indicates data unavailable (uses neutral scoring)'),
    // Optional fields that may be present
    position: zod_1.z.number().nullable().optional().describe('Current ranking position'),
    trafficPercent: zod_1.z.number().nullable().optional().describe('Traffic percentage'),
    url: zod_1.z.string().nullable().optional().describe('Ranking URL'),
});
exports.seoKeywordOpportunityRankerArgsSchema = zod_1.z.object({
    keywordSources: zod_1.z
        .array(zod_1.z.array(keywordDataSchema))
        .min(1)
        .describe('Array of keyword data arrays from keyword research tools. Each array contains keywords with unified schema: keyword, searchVolume, keywordDifficulty, costPerClick, competition, searchIntent'),
    businessStage: zod_1.z
        .enum(['pre-launch', 'foundation', 'growth', 'advanced'])
        .default('foundation')
        .describe('Business SEO maturity stage: pre-launch (new domain), foundation (basic SEO), growth (active program), advanced (strategic execution)'),
    maxResults: zod_1.z
        .number()
        .min(5)
        .max(100)
        .default(20)
        .describe('Maximum number of ranked opportunities to return (default: 20)'),
    minSearchVolume: zod_1.z
        .number()
        .min(0)
        .default(100)
        .describe('Minimum monthly search volume threshold (default: 100)'),
    maxDifficulty: zod_1.z
        .number()
        .min(0)
        .max(100)
        .default(70)
        .describe('Maximum keyword difficulty threshold (default: 70)'),
    prioritizeIntent: zod_1.z
        .array(zod_1.z.string())
        .default(['transactional', 'commercial', 'informational'])
        .describe('Search intent priority order (default: transactional, commercial, informational)'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
});
// Output schema for ranked keyword opportunities
var rankedKeywordSchema = zod_1.z.object({
    keyword: zod_1.z.string().describe('Keyword phrase'),
    searchVolume: zod_1.z.number().describe('Monthly search volume'),
    keywordDifficulty: zod_1.z.number().describe('Keyword difficulty (0-100)'),
    costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
    competition: zod_1.z.number().describe('Competition level (0-1)'),
    searchIntent: zod_1.z.string().describe('Search intent classification'),
    opportunityScore: zod_1.z.number().min(0).max(100).describe('Calculated opportunity score (0-100)'),
    opportunityTier: zod_1.z
        .enum(['high', 'medium', 'low'])
        .describe('Opportunity tier based on score: high (80+), medium (60-79), low (<60)'),
    quickWin: zod_1.z.boolean().describe('Whether this is identified as a quick-win opportunity'),
    reasoning: zod_1.z.string().describe('Explanation of why this keyword received its opportunity score'),
    // Optional source context fields
    position: zod_1.z.number().nullable().optional().describe('Current ranking position if available'),
    trafficPotential: zod_1.z.number().nullable().optional().describe('Estimated traffic potential'),
});
exports.seoKeywordOpportunityRankerResultSchema = zod_1.z.object({
    rankedKeywords: zod_1.z
        .array(rankedKeywordSchema)
        .describe('Keywords ranked by opportunity score (highest to lowest) - primary output compatible with native tools'),
    summary: zod_1.z
        .object({
        totalProcessed: zod_1.z.number().describe('Total number of keywords processed'),
        totalReturned: zod_1.z.number().describe('Number of keywords returned after filtering'),
        highOpportunities: zod_1.z.number().describe('Number of high opportunity keywords (score 80+)'),
        mediumOpportunities: zod_1.z
            .number()
            .describe('Number of medium opportunity keywords (score 60-79)'),
        lowOpportunities: zod_1.z.number().describe('Number of low opportunity keywords (score <60)'),
        quickWins: zod_1.z.number().describe('Number of quick-win opportunities identified'),
        averageScore: zod_1.z.number().describe('Average opportunity score of returned keywords'),
        totalSearchVolume: zod_1.z.number().describe('Combined search volume of all returned keywords'),
    })
        .optional()
        .describe('Optional summary statistics for analysis'),
    methodology: zod_1.z
        .object({
        scoringFormula: zod_1.z.string().describe('Description of the opportunity scoring algorithm used'),
        businessStageAdjustments: zod_1.z
            .string()
            .describe('How business stage affected difficulty thresholds and scoring'),
        intentPrioritization: zod_1.z.string().describe('How search intent was prioritized in scoring'),
    })
        .optional()
        .describe('Optional methodology explanation'),
    metadata: zod_1.z
        .object({
        executedAt: zod_1.z.string().datetime(),
        processingTime: zod_1.z.number().describe('Processing time in milliseconds'),
        businessStage: zod_1.z.string().describe('Business stage used for scoring adjustments'),
        filterCriteria: zod_1.z.object({
            minSearchVolume: zod_1.z.number().describe('Minimum search volume filter applied'),
            maxDifficulty: zod_1.z.number().describe('Maximum difficulty filter applied'),
            intentPriority: zod_1.z.array(zod_1.z.string()).describe('Intent priority order used'),
        }),
    })
        .optional()
        .describe('Optional execution metadata'),
});
exports.seoKeywordOpportunityRanker = (0, common_1.createEmitModeToolMetadata)({
    id: 'seoKeywordOpportunityRanker',
    displayName: 'SEO Keyword Opportunity Ranker',
    displayDescription: 'Rank and score keyword opportunities from keyword research tools using proven SEO algorithms. Applies business-stage-specific scoring, identifies quick wins, and returns prioritized keyword opportunities. Handles placeholder values gracefully: competition=-1 (ignored in scoring), searchIntent="unknown" (neutral scoring).',
    category: 'seo',
    color: common_1.BRAND_COLORS.SPRITES,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.SPRITES),
    icon: 'target',
    argsSchema: exports.seoKeywordOpportunityRankerArgsSchema,
}, exports.seoKeywordOpportunityRankerResultSchema);
